import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from 'app/lib/utils'

const Tabs = TabsPrimitive.Root

/**
 * `segmented` (default): a pill of mutually exclusive choices, the selected one raised, to
 * match `SegmentedControl`. `line`: no pill or selected style of its own, for navigation
 * bars that draw their own indicator (`NavTabs` draws an underline).
 */
type TabsListVariant = 'segmented' | 'line'

const LIST_VARIANT_CLASS: Record<TabsListVariant, string> = {
  segmented: 'h-9 justify-center gap-0.5 rounded-lg border border-default bg-surface-subtle p-1 text-subtle',
  line: '',
}

function TabsList({
  className,
  variant = 'segmented',
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & { variant?: TabsListVariant }) {
  return (
    <TabsPrimitive.List
      ref={ref}
      data-variant={variant}
      className={cn('group/tabs-list inline-flex items-center', LIST_VARIANT_CLASS[variant], className)}
      {...props}
    />
  )
}

// The selected style is scoped to segmented lists through the list's data-variant, so a
// `line` list's own indicator never competes with it and needs no overrides to undo it.
function TabsTrigger({ className, ref, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        'group-data-[variant=segmented]/tabs-list:hover:text-strong',
        'group-data-[variant=segmented]/tabs-list:data-[state=active]:bg-surface group-data-[variant=segmented]/tabs-list:data-[state=active]:font-medium group-data-[variant=segmented]/tabs-list:data-[state=active]:text-strong group-data-[variant=segmented]/tabs-list:data-[state=active]:shadow-elevation-1',
        className,
      )}
      {...props}
    />
  )
}

function TabsContent({ className, ref, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
